import { useState } from 'react';

import { ClickableText, type ModalChildrenProps } from '@bitstopco/bitstop-theme';

import { LoadingButton } from '@mui/lab';
import {
  Alert,
  Box,
  Button,
  Chip,
  DialogActions,
  DialogContent,
  Divider,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import {
  useConfirmCashLogMutation,
  useSetCashLogStatusMutation,
} from '@/store/api/services/machines/cashlogs';

import { cashLogStatusLabel } from '@/helpers/cashLogs';

import type { MachinesCashLog } from '@/types';
import type { CashLogStatus } from '@/types/machine';

type BulkAction =
  | 'confirm'
  | 'setProvisional'
  | 'setDeposited'
  | 'setDepositedOver'
  | 'setDepositedUnder'
  | 'setVoided';

interface BulkResult {
  id: number;
  machine: string;
  success: boolean;
  error?: string;
}

const COPY: Record<
  BulkAction,
  { verb: string; past: string; intro: string; status: CashLogStatus; skipped: string }
> = {
  confirm: {
    verb: 'Confirm',
    past: 'confirmed',
    intro: 'These provisional clears will be marked as confirmed.',
    status: 'confirmed',
    skipped: 'already-confirmed',
  },
  setProvisional: {
    verb: 'Set to Provisional',
    past: 'reverted to provisional',
    intro: 'These confirmed clears will be reverted to provisional.',
    status: 'provisional',
    skipped: 'already-provisional',
  },
  setDeposited: {
    verb: 'Set to Deposited',
    past: 'set to deposited',
    intro: 'These clears will be marked as deposited (deposit matched the cash log amount).',
    status: 'deposited',
    skipped: 'ineligible',
  },
  setDepositedOver: {
    verb: 'Set to Deposited Over',
    past: 'set to deposited over',
    intro:
      'These clears will be marked as deposited over (deposit was more than the cash log amount).',
    status: 'deposited_over',
    skipped: 'ineligible',
  },
  setDepositedUnder: {
    verb: 'Set to Deposited Under',
    past: 'set to deposited under',
    intro:
      'These clears will be marked as deposited under (deposit was less than the cash log amount).',
    status: 'deposited_under',
    skipped: 'ineligible',
  },
  setVoided: {
    verb: 'Set to Voided',
    past: 'set to voided',
    intro: 'These clears will be marked as voided (they will never be deposited).',
    status: 'voided',
    skipped: 'ineligible',
  },
};

/**
 * Pulls a user-readable message out of an RTK-Query rejection. Mutations
 * reject with `FetchBaseQueryError | SerializedError`; for transient/server
 * errors the payload usually carries `data.message` or `data.error`.
 */
const extractError = (reason: unknown): string => {
  if (!reason) return 'Unknown error';
  if (typeof reason === 'string') return reason;
  if (typeof reason === 'object') {
    const r = reason as { data?: { message?: string; error?: string }; message?: string };
    return r.data?.message || r.data?.error || r.message || 'Unknown error';
  }
  return String(reason);
};

/**
 * Bulk Update Cash Logs modal — handles bulk Confirm and bulk Set-to-Provisional.
 *
 * Mirrors the BulkSetMachineStatus pattern: shows a confirmation table of the
 * affected clears, then a per-row results table (success / error) after the
 * fan-out completes. The boro API exposes no bulk endpoint for these status
 * changes, so requests are fanned out with `Promise.allSettled`.
 */
const BulkUpdateCashLogs = ({ hide, props }: ModalChildrenProps) => {
  const {
    rows = [],
    action,
    skipped = 0,
  } = (props ?? {}) as { rows?: MachinesCashLog[]; action?: BulkAction; skipped?: number };
  // `props` becomes undefined during the modal's hide transition — bail out
  // before reaching `COPY[action]`, which would otherwise blow up the
  // closing-animation render.
  const copy = action ? COPY[action] : undefined;
  const ineligibleStatus = copy?.skipped;

  const [confirmCashLog] = useConfirmCashLogMutation();
  const [setCashLogStatus] = useSetCashLogStatusMutation();

  const [results, setResults] = useState<BulkResult[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const done = results.length > 0;

  const handleSubmit = async () => {
    if (!copy) return;
    setSubmitting(true);
    const mutate = (id: number) =>
      action === 'confirm' ? confirmCashLog({ id }) : setCashLogStatus({ id, status: copy.status });
    const settled = await Promise.allSettled(rows.map((r) => mutate(r.id).unwrap()));
    setResults(
      rows.map((r, i) => {
        const outcome = settled[i];
        const machine = r.atm?.name ?? `#${r.atm_id}`;
        return outcome.status === 'fulfilled'
          ? { id: r.id, machine, success: true }
          : { id: r.id, machine, success: false, error: extractError(outcome.reason) };
      }),
    );
    setSubmitting(false);
  };

  const successCount = results.filter((r) => r.success).length;
  const failedCount = results.length - successCount;

  if (!copy) return null;

  return (
    <>
      <DialogContent sx={{ py: 2 }}>
        {!done ? (
          <>
            <Typography variant="body2" color="text.secondary" mb={2}>
              {copy.intro} {rows.length} clear(s) will be updated.
            </Typography>
            {skipped > 0 && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {skipped} {ineligibleStatus} clear(s) in your selection were excluded — only
                eligible clears are shown below.
              </Alert>
            )}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Clear ID</TableCell>
                    <TableCell>Machine</TableCell>
                    <TableCell>Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((row) => (
                    <TableRow key={row.id}>
                      <TableCell>
                        <ClickableText
                          fixIcon
                          name="Clear ID"
                          text={`${row.id}`}
                          clipboardValue={String(row.id)}
                          to={`/cash-log/${row.id}`}
                        />
                      </TableCell>
                      <TableCell>{row.atm?.name ?? `#${row.atm_id}`}</TableCell>
                      <TableCell sx={{ textTransform: 'capitalize' }}>
                        {cashLogStatusLabel(row.status)}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        ) : (
          <>
            <Stack direction="row" gap={1} alignItems="center" mb={2}>
              <Typography variant="h6">Results</Typography>
              <Chip
                size="small"
                color="success"
                label={`${successCount} ${copy.past}`}
                sx={{ textTransform: 'none' }}
              />
              {failedCount > 0 && (
                <Chip
                  size="small"
                  color="error"
                  label={`${failedCount} failed`}
                  sx={{ textTransform: 'none' }}
                />
              )}
            </Stack>
            <Divider sx={{ mb: 2 }} />
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Clear ID</TableCell>
                    <TableCell>Machine</TableCell>
                    <TableCell>Result</TableCell>
                    <TableCell>Error</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {results.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell>
                        <ClickableText
                          fixIcon
                          name="Clear ID"
                          text={`#${r.id}`}
                          clipboardValue={String(r.id)}
                          to={`/cash-log/${r.id}`}
                        />
                      </TableCell>
                      <TableCell>{r.machine}</TableCell>
                      <TableCell>
                        <Box
                          component="span"
                          sx={{ color: r.success ? 'success.main' : 'error.main', fontWeight: 600 }}
                        >
                          {r.success ? 'Success' : 'Failed'}
                        </Box>
                      </TableCell>
                      <TableCell sx={{ color: 'text.secondary' }}>{r.error || '-'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </DialogContent>
      <Divider />
      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {!done ? (
          <>
            <Button onClick={hide}>Cancel</Button>
            <LoadingButton
              variant="contained"
              loading={submitting}
              disabled={rows.length === 0}
              onClick={handleSubmit}
              data-test="cash-log-bulk-submit"
            >
              {copy.verb} {rows.length} clear(s)
            </LoadingButton>
          </>
        ) : (
          <Button fullWidth variant="contained" onClick={hide}>
            Back to Cash Logs
          </Button>
        )}
      </DialogActions>
    </>
  );
};

export default BulkUpdateCashLogs;
